import {
  type ProjectMinimizedCollaboratorsList_portfolioProject$data,
  type ProjectMinimizedCollaboratorsList_portfolioProject$key,
} from '#app/__generated__/relay/ProjectMinimizedCollaboratorsList_portfolioProject.graphql.js';
import { MinimizedCollaboratorsList } from '#app/components/MinimizedCollaboratorsList/MinimizedCollaboratorsList.js';
import { type MinimizedCollaboratorsListData } from '#app/components/MinimizedCollaboratorsList/types.js';
import { graphql, useFragment } from '@contra/relay';
import { useMemo } from 'react';

const getDataForMinimizedCollaboratorsList = (
  collaborators: ProjectMinimizedCollaboratorsList_portfolioProject$data['collaborators'],
): MinimizedCollaboratorsListData => {
  return collaborators.edges.map(({ node: collaborator }) => {
    if (collaborator.__typename === 'ExistingUserCollaborator') {
      return {
        avatarImage: collaborator.userProfile?.avatarImage,
        displayUsername: collaborator?.userProfile?.displayUsername,
        isMasked: collaborator.status !== 'CONFIRMED',
        key: collaborator.id,
      };
    } else if (collaborator.__typename === 'NewUserCollaborator') {
      return {
        emailAddress: collaborator.emailAddress,
        isMasked: collaborator.status !== 'CONFIRMED',
        key: collaborator.name,
        name: collaborator.name,
      };
    }

    throw new Error('Unknown collaborator type');
  });
};

type ProjectMinimizedCollaboratorsListProps = {
  readonly collaboratorsRef: ProjectMinimizedCollaboratorsList_portfolioProject$key;
  readonly onSeeMore: () => void;
};

export const ProjectMinimizedCollaboratorsList = ({
  collaboratorsRef,
  onSeeMore,
}: ProjectMinimizedCollaboratorsListProps) => {
  const data =
    useFragment<ProjectMinimizedCollaboratorsList_portfolioProject$key>(
      graphql`
        fragment ProjectMinimizedCollaboratorsList_portfolioProject on PortfolioProject {
          collaborators {
            edges {
              node {
                __typename

                ... on NewUserCollaborator {
                  emailAddress
                  name
                  status
                }

                ... on ExistingUserCollaborator {
                  id
                  status
                  userProfile {
                    displayUsername
                    avatarImage {
                      id
                      uid
                      url
                    }
                  }
                }
              }
            }
          }
        }
      `,
      collaboratorsRef,
    );

  const { collaborators } = data;

  const collaboratorsList = useMemo(
    () => getDataForMinimizedCollaboratorsList(collaborators),
    [collaborators],
  );

  if (!collaboratorsList.length) return null;

  return (
    <MinimizedCollaboratorsList
      collaborators={collaboratorsList}
      onSeeMore={onSeeMore}
    />
  );
};
